import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { PlaceMenu } from '@shared/types';

/**
 * Loads a restaurant's menu lazily.
 *
 * Only fetches when `enabled` is true, so opening a place row does not pull a
 * menu the user never asked to see — the menu sheet flips this on when it
 * actually opens. The result is cached in state for the component's lifetime,
 * so reopening the same sheet does not refetch.
 */
export function usePlaceMenu(slug: string | null, enabled: boolean) {
  const [menu, setMenu] = useState<PlaceMenu | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!enabled || !slug) return;
    // Already have this slug's menu — don't refetch on reopen.
    if (menu?.placeSlug === slug) return;

    let cancelled = false;
    setLoading(true);
    setError(false);
    api
      .get<{ menu: PlaceMenu }>(`/places/${encodeURIComponent(slug)}/menu`)
      .then((res) => {
        if (!cancelled) setMenu(res.menu);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, enabled, menu?.placeSlug]);

  return { menu, loading, error };
}
